<template>
  <div>
    <EmptyLayout>
      <template #header__left>
        <slot name="header__left"></slot>
      </template>
      <template #header__center>
        <slot name="header__center"></slot>
      </template>
      <template #header__right>
        <slot name="header__right"></slot>
      </template>
      <template #main__center__header>
        <slot name="main__center__header"></slot>
      </template>
      <template #main__center>
        <div
          ref="mainCenter"
          class="main__center-container"
        >
          <MapRasterView
            v-if="!mapVectorVisible && !!props___.mapConfig"
            :config-map="props___.mapConfig"
          />
          <MapVectorView
            v-if="mapVectorVisible && !!props___.mapConfig"
            :config-map="props___.mapConfig"
          />
          <div class="overlay-panel hide-arrow">
            <router-view v-slot="{ Component }">
              <OverlayPanel
                ref="oPanel"
                :class="{ 'minified-panel': minifiedPanel }"
                append-to="div.overlay-panel"
                :dismissable="false"
                :show-close-icon="true"
                :auto-z-index="false"
                @hide="goHome"
              >
                <component :is="Component" />
              </OverlayPanel>
            </router-view>
          </div>
          <slot name="main__center"></slot>
        </div>
      </template>
      <template #footer>
        <slot name="footer"></slot>
      </template>
    </EmptyLayout>
    <div class="sidebar-panel md:hidden">
      <slot name="sidebar"></slot>
    </div>
  </div>
</template>
<script setup>
import {ref, onMounted, watch, computed} from 'vue';
import {useStore} from 'vuex';
import {useRoute, useRouter} from 'vue-router';
import OverlayPanel from 'primevue/overlaypanel';
import EmptyLayout from '@/templates/Empty';
import MapRasterView from '@/pages/_root/views/map/Map';
import MapVectorView from '@/pages/_root/views/map-vector/MapVector';

// #region Props, Emits
// #endregion
// #region Params, Ref, Reactives, Computed
const route = useRoute();
const router = useRouter();
const store = useStore();

const props___ = defineProps({
  mapType: {
    type: String,
    default: 'vector',
  },
  mapConfig: {
    type: Object,
    default: null,
  },
});

const mainCenter = ref();
const oPanel = ref();

const mapVectorVisible = computed(() => mapVectorVisibility());
const minifiedPanel = computed(()=> store.state.app.overlayMinification);
// #endregion

// #region Functions
function mapVectorVisibility() {
  if (!!props___.mapType) {
    if (props___.mapType === 'vector') {
      return true;
    }
    if (props___.mapType === 'raster') {
      return false;
    }
  }
  return true;
}
async function goHome() {
  await router.push({name: 'Home'});
}
async function showHidePanel(op) {
  if (route.name === 'Home') {
    await hidePanel(op);
  } else {
    await showPanel(op);
  }
}
async function showPanel(op) {
  if (!!op?.show && !!mainCenter.value) {
    op.show(new Event('show'), mainCenter.value);
  }
}
async function hidePanel(op) {
  if (!!op?.hide) {
    op.hide();
  }
}
// #endregion
// #region Effects, Hooks
onMounted(async () => {
  await showHidePanel(oPanel.value);
});
watch(
  [oPanel, route],
  async ([newPanel, _newRoute], [_oldPanel, _oldRoute]) => {
    await showHidePanel(newPanel);
  },
);
// #endregion
// #region Provide, Expose
// #endregion
</script>
<style lang="scss" scoped>
@use "src/styles/base";
@import "@/styles/custom-colors.scss";
.main__center-container {
  flex: 0 0 100%;
}
.sidebar-panel {
  position: absolute;
  top: $brand__boxspace--headerheight;
  right: 0;
  padding: 0;
  margin: 0;
  background-color: white;
  z-index: 99;
}

/* custom styles @overlayPanel */
.overlay-panel :deep(.p-overlaypanel) {
  right: 0; /* left: 0 and right: 0 are needed for centering */
  margin-left: auto;
  margin-right: auto;
  width: 99%;
  z-index: 1;
  @include base.min-screen-width($brand__breakpoint--lg) {
    width: 80%;
  }
  .p-button-secondary {
    border: none;
    background-color: transparent !important;
  }
  .data-view-list__item>.p-card-body>.p-card-content {
    padding: 1rem !important;
  }
  .data-view-list__item>.p-card-body>.p-card-footer .data-view-actions-combo {
    padding: 1rem !important;
    background: $subtle-blue_60;
  }
  .data-view-list__item:hover,
  .p-datatable-wrapper>.p-datatable-table>.p-datatable-tbody>tr:hover {
    background-color: $subtle-blue !important;
  }
  .p-message-wrapper {
    background-color: $subtle-blue_60 !important;
  }
}
.overlay-panel :deep(.p-overlaypanel-content),
.overlay-panel :deep(.p-accordion-content) {
  padding: 1%;
}
.overlay-panel :deep(.p-overlaypanel .smart-content__center) {
  padding: 0;
}
.overlay-panel :deep(.p-overlaypanel .smart-content__main .data-view) {
  max-height: 70dvh;

}
.overlay-panel :deep(.p-overlaypanel-close) {
  border-radius: 50%;
  background: transparent;
  color: $solid-blue;
  top: 1.4rem;
  right: 2.0%;
  z-index: 1;
}
.overlay-panel :deep(.p-overlaypanel-close:focus) {
  box-shadow: none !important;
}
.overlay-panel :deep(.p-overlaypanel-close-icon) {
  font-size: 1.3rem !important;
  font-weight: 800 !important;
}
.overlay-panel :deep(.p-accordion-header-link) {
  text-align: left !important;
  padding-left: 1vw;
  background: $subtle-blue_80 !important;
  .p-accordion{
    &-toggle-icon, &-header-text  {
      font-size: 1.1rem;
      color: $solid-blue;
    }
    .p-accoridon-tab {
      margin-bottom: 0 !important;
    }
  }
}
.overlay-panel :deep(.p-accordion-header-link:hover) {
  background: $subtle-blue !important;
}
.overlay-panel :deep(.p-datatable-striped tr) {
  background: transparent !important;
}

.overlay-panel :deep(.data-view__header) {
  padding: .5rem;
  background-color: $subtle-blue_60 !important;
  .p-inputtext, .p-dropdown {
    background-color: transparent !important;
  }
}
</style>
